import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ShoppingCart, Plus, Download, Edit3, Trash2, Package, Truck, CheckCircle2,
  Building2, FileText, Search, Clock, ArrowRight, Save, DollarSign, Shield,
  Send, User, X, ChevronLeft
} from 'lucide-react';
import { toast } from 'sonner';
import ShareDocDialog from '../components/ShareDocDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { n: 1, key: 'fournisseur', label: 'Fournisseur', icon: Building2, color: '#2563eb' },
  { n: 2, key: 'bon',         label: 'Bon de commande', icon: FileText, color: '#2563eb' },
  { n: 3, key: 'articles',    label: 'Articles', icon: Package, color: '#2563eb' },
  { n: 4, key: 'validation',  label: 'Validation', icon: Shield, color: '#2563eb' },
  { n: 5, key: 'paiement',    label: 'Paiement', icon: DollarSign, color: '#2563eb' },
  { n: 6, key: 'expedition',  label: 'Expédition', icon: Send, color: '#2563eb' },
  { n: 7, key: 'livraison',   label: 'Livraison', icon: Truck, color: '#2563eb' },
];

const STATUS_LABELS = {
  brouillon: { label: 'Brouillon', color: 'bg-slate-500' },
  envoyee: { label: 'Envoyée', color: 'bg-blue-500' },
  en_production: { label: 'En production', color: 'bg-amber-500' },
  livree: { label: 'Livrée', color: 'bg-emerald-500' },
  annulee: { label: 'Annulée', color: 'bg-red-500' },
};

const emptyForm = () => ({
  // Fournisseur
  fournisseur_nom: '',
  fournisseur_code: '',
  fournisseur_contact: '',
  fournisseur_email: '',
  fournisseur_telephone: '',
  fournisseur_adresse: '',
  // Bon
  date_commande: new Date().toISOString().slice(0, 10),
  devise: 'XOF - Franc CFA',
  conditions_paiement: '30 jours fin de mois',
  date_echeance: '',
  reference_fournisseur: '',
  // Articles
  lignes: [{ reference: '', designation: '', unite: 'Pce', quantite: 1, prix_unitaire: 0, remise_pct: 0, tva_pct: 18 }],
  // Validation
  validation_statut: 'en_attente',
  validation_par: '',
  validation_date: '',
  validation_commentaires: '',
  // Paiement
  paiement_statut: 'non_paye',
  paiement_mode: 'Virement bancaire',
  paiement_acompte: 0,
  // Expédition
  expedition_statut: 'en_attente',
  date_expedition_prevue: '',
  transporteur: '',
  numero_suivi: '',
  // Livraison
  livraison_statut: 'non_livree',
  date_livraison_prevue: '',
  date_livraison_reelle: '',
  lieu_livraison: '',
  livraison_contact_nom: '',
  livraison_contact_tel: '',
  livraison_incoterm: '',
  livre_par: '',
  receptionne_par: '',
  // Notes
  observations: '',
  notes: '',
  statut: 'brouillon',
});

const fmtXAF = (n) => `${Math.round(Number(n || 0)).toLocaleString('fr-FR').replace(/,/g, ' ')}`;

// ─────────────────────────────────────────────────────────────────────────────
// Vue LISTE
// ─────────────────────────────────────────────────────────────────────────────
function ListView({ items, onNew, onOpen, onDelete, onPdf, onSend }) {
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');

  const filtered = items.filter(bc => {
    if (filterStatut !== 'all' && bc.statut !== filterStatut) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (bc.numero || '').toLowerCase().includes(q) ||
      (bc.fournisseur_nom || '').toLowerCase().includes(q);
  });

  const kpis = {
    total: items.length,
    en_cours: items.filter(x => ['envoyee', 'en_production'].includes(x.statut)).length,
    livrees: items.filter(x => x.statut === 'livree').length,
    montant: items.reduce((s, x) => s + Number(x.total_ttc || 0), 0),
  };

  return (
    <div className="space-y-6" data-testid="saisie-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 bg-gradient-to-br from-[#0B1E42] to-[#1e3a8a] rounded-xl flex items-center justify-center">
            <ShoppingCart className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-[#0B1E42]">Saisie de commande</h1>
            <p className="text-sm text-gray-500">Fournisseur · Bon de commande · Articles · Validation · Paiement · Expédition · Livraison</p>
          </div>
        </div>
        <Button onClick={onNew} className="gap-2 bg-[#0B1E42] hover:bg-[#1e3a8a] text-white h-11 px-5" data-testid="btn-new-commande">
          <Plus className="h-4 w-4" /> Nouveau bon de commande
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { lab: 'Total commandes', val: kpis.total, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-100' },
          { lab: 'En cours', val: kpis.en_cours, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-100' },
          { lab: 'Livrées', val: kpis.livrees, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100' },
          { lab: 'Montant total TTC', val: fmtXAF(kpis.montant) + ' FCFA', icon: Package, color: 'text-violet-600', bg: 'bg-violet-100', big: true },
        ].map(k => {
          const Ic = k.icon;
          return (
            <Card key={k.lab} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${k.bg} flex items-center justify-center flex-shrink-0`}>
                  <Ic className={`h-6 w-6 ${k.color}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide truncate">{k.lab}</p>
                  <p className={`${k.big ? 'text-lg' : 'text-2xl'} font-bold text-[#0B1E42] leading-tight truncate`}>{k.val}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardContent className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="commande-search-input" />
          </div>
          <Select value={filterStatut} onValueChange={setFilterStatut}>
            <SelectTrigger className="w-full sm:w-64" data-testid="commande-status-filter">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ShoppingCart className="h-14 w-14 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-gray-700">Aucun bon de commande</h3>
            <p className="text-sm text-gray-500 mt-1">Créez votre premier bon de commande pour démarrer.</p>
            <Button onClick={onNew} className="mt-4 bg-[#0B1E42] hover:bg-[#1e3a8a] text-white gap-2">
              <Plus className="h-4 w-4" /> Créer un bon de commande
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="commande-list">
          {filtered.map(bc => {
            const st = STATUS_LABELS[bc.statut] || STATUS_LABELS.brouillon;
            return (
              <Card key={bc.id} className="hover:shadow-md transition-shadow" data-testid={`commande-card-${bc.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => onOpen(bc)}>
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <Badge className="bg-[#0B1E42] text-white">{bc.numero}</Badge>
                        <Badge className={`${st.color} text-white`}>{st.label}</Badge>
                        <span className="font-semibold text-gray-900 truncate">{bc.fournisseur_nom || 'Fournisseur non renseigné'}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Commande du {bc.date_commande ? new Date(bc.date_commande).toLocaleDateString('fr-FR') : '—'}
                        {bc.date_livraison_prevue ? ` • Livraison : ${bc.date_livraison_prevue}` : ''}
                        {bc.lignes?.length ? ` • ${bc.lignes.length} article(s)` : ''}
                      </div>
                    </div>
                    <div className="flex flex-col md:items-end gap-2">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Total TTC</div>
                        <div className="text-xl font-bold text-[#0B1E42]">{fmtXAF(bc.total_ttc)} FCFA</div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onPdf(bc); }} data-testid={`btn-pdf-${bc.id}`}>
                          <Download className="h-4 w-4 mr-1" /> PDF
                        </Button>
                        <Button size="sm" onClick={(e) => { e.stopPropagation(); onSend(bc); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid={`btn-send-${bc.id}`}>
                          <Send className="h-4 w-4 mr-1" /> Envoyer
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onOpen(bc); }} data-testid={`btn-edit-${bc.id}`}>
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onDelete(bc.id); }} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`btn-delete-${bc.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Vue ÉDITEUR (7 étapes)
// ─────────────────────────────────────────────────────────────────────────────
function EditorView({ initial, editingId, onCancel, onSaved }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(() => ({ ...emptyForm(), ...(initial || {}) }));
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [searchParams] = useSearchParams();

  // Charge la liste des fournisseurs pour le sélecteur
  useEffect(() => {
    axios.get(`${API}/api/fournisseurs`).then(r => setFournisseurs(r.data || [])).catch(() => {});
  }, []);

  // Pré-remplissage depuis ?fournisseur=CODE (depuis la fiche fournisseur)
  useEffect(() => {
    const codeParam = searchParams.get('fournisseur');
    if (codeParam && fournisseurs.length && !form.fournisseur_code) {
      const f = fournisseurs.find(x => x.code === codeParam);
      if (f) applyFournisseur(f);
    }
    // eslint-disable-next-line
  }, [searchParams, fournisseurs]);

  const applyFournisseur = (f) => {
    setForm(prev => ({
      ...prev,
      fournisseur_nom: f.nom || '',
      fournisseur_code: f.code || '',
      fournisseur_contact: f.contact_nom || '',
      fournisseur_email: f.email || '',
      fournisseur_telephone: f.telephone || '',
      fournisseur_adresse: [f.adresse, f.ville, f.pays].filter(Boolean).join(', '),
      devise: f.devise || prev.devise,
      conditions_paiement: f.conditions_paiement || prev.conditions_paiement,
    }));
  };

  const totals = (() => {
    const lignes = form.lignes || [];
    let sous_total = 0, tva_total = 0;
    lignes.forEach(l => {
      const pu = Number(l.prix_unitaire || 0);
      const qt = Number(l.quantite || 0);
      const remise = Number(l.remise_pct || 0);
      const tva = Number(l.tva_pct || 0);
      const ht = pu * qt * (1 - remise / 100);
      sous_total += ht;
      tva_total += ht * (tva / 100);
    });
    return {
      sous_total,
      tva: tva_total,
      total_ttc: sous_total + tva_total,
      reste: Math.max(0, sous_total + tva_total - Number(form.paiement_acompte || 0)),
    };
  })();

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setLigne = (idx, k, v) => {
    const next = [...form.lignes];
    next[idx] = { ...next[idx], [k]: v };
    set('lignes', next);
  };
  const addLigne = () => set('lignes', [...form.lignes, { reference: '', designation: '', unite: 'Pce', quantite: 1, prix_unitaire: 0, remise_pct: 0, tva_pct: 18 }]);
  const removeLigne = (idx) => set('lignes', form.lignes.filter((_, i) => i !== idx));

  const save = async () => {
    if (!form.fournisseur_nom.trim()) {
      toast.error('Le nom du fournisseur est requis');
      setCurrentStep(1);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lignes: form.lignes.map(l => ({
          ...l,
          quantite: Number(l.quantite || 0),
          prix_unitaire: Number(l.prix_unitaire || 0),
          remise_pct: Number(l.remise_pct || 0),
          tva_pct: Number(l.tva_pct || 0),
        })),
        paiement_acompte: Number(form.paiement_acompte || 0),
      };
      const res = editingId
        ? await axios.put(`${API}/api/commandes/${editingId}`, payload)
        : await axios.post(`${API}/api/commandes`, payload);
      toast.success(editingId ? 'Bon de commande mis à jour' : `Bon de commande ${res.data.numero} créé`);
      onSaved(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async () => { await save(); };

  const next = () => setCurrentStep(s => Math.min(7, s + 1));

  return (
    <div className="space-y-6" data-testid="saisie-editor">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onCancel} className="gap-2" data-testid="btn-back-list">
            <ChevronLeft className="h-4 w-4" /> Retour
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#0B1E42] to-[#1e3a8a] rounded-xl flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#0B1E42]">Saisie de commande</h1>
              <p className="text-xs text-gray-500">{editingId ? `Édition ${form.numero || ''}` : 'Nouvelle commande'}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={saveDraft} disabled={saving} className="gap-2" data-testid="btn-save-draft">
            <Save className="h-4 w-4" /> Enregistrer le brouillon
          </Button>
          <Button onClick={currentStep === 7 ? save : next} disabled={saving} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="btn-next-or-save">
            {currentStep === 7 ? (
              <>{saving ? 'Enregistrement…' : 'Enregistrer'} <CheckCircle2 className="h-4 w-4" /></>
            ) : (
              <>Suivant <ArrowRight className="h-4 w-4" /></>
            )}
          </Button>
        </div>
      </div>

      {/* Stepper */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const done = s.n < currentStep;
              const active = s.n === currentStep;
              return (
                <div key={s.n} className="flex-1 flex items-center">
                  <button
                    onClick={() => setCurrentStep(s.n)}
                    className="flex flex-col items-center gap-1 group"
                    data-testid={`step-${s.n}-btn`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all
                      ${active ? 'bg-blue-600 text-white border-blue-600 shadow-md scale-110' :
                        done ? 'bg-blue-600 text-white border-blue-600' :
                          'bg-white text-gray-400 border-gray-300 group-hover:border-blue-400'}`}>
                      {done ? <CheckCircle2 className="h-5 w-5" /> : s.n}
                    </div>
                    <span className={`text-xs font-semibold ${active ? 'text-blue-600' : done ? 'text-[#0B1E42]' : 'text-gray-500'}`}>
                      {s.label}
                    </span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${s.n < currentStep ? 'bg-blue-600' : 'bg-gray-300'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Layout principal : contenu + sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-9 space-y-4">
          {/* Row 1 : 1 Fournisseur + 2 Bon + 3 Articles (span 2) + 4 Validation */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
            {/* 1. Fournisseur */}
            <StepCard n={1} title="Fournisseur" icon={Building2} active={currentStep === 1} tint="bg-blue-50">
              <div className="space-y-2">
                <Field label="Informations fournisseur" muted />
                <Field label="Fournisseur *">
                  {fournisseurs.length > 0 ? (
                    <Select
                      value={form.fournisseur_code || ''}
                      onValueChange={(code) => {
                        if (code === '__new__') {
                          window.open('/fournisseurs', '_blank');
                          return;
                        }
                        const f = fournisseurs.find(x => x.code === code);
                        if (f) applyFournisseur(f);
                      }}
                    >
                      <SelectTrigger data-testid="select-fournisseur">
                        <SelectValue placeholder="Sélectionner un fournisseur" />
                      </SelectTrigger>
                      <SelectContent>
                        {fournisseurs.map(f => (
                          <SelectItem key={f.id} value={f.code}>{f.code} · {f.nom}</SelectItem>
                        ))}
                        <SelectItem value="__new__">➕ Créer un nouveau fournisseur…</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={form.fournisseur_nom} onChange={e => set('fournisseur_nom', e.target.value)} placeholder="AFRICASUP SARL" data-testid="input-fournisseur-nom" />
                  )}
                </Field>
                <Field label="Code fournisseur">
                  <Input value={form.fournisseur_code} onChange={e => set('fournisseur_code', e.target.value)} placeholder="F00025" />
                </Field>
                <Field label="Contact">
                  <Input value={form.fournisseur_contact} onChange={e => set('fournisseur_contact', e.target.value)} placeholder="M. Karim Benali" />
                </Field>
                <Field label="Téléphone">
                  <Input value={form.fournisseur_telephone} onChange={e => set('fournisseur_telephone', e.target.value)} />
                </Field>
                <Field label="Email">
                  <Input type="email" value={form.fournisseur_email} onChange={e => set('fournisseur_email', e.target.value)} />
                </Field>
                <Field label="Adresse">
                  <Textarea rows={2} value={form.fournisseur_adresse} onChange={e => set('fournisseur_adresse', e.target.value)} />
                </Field>
                {form.fournisseur_code && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full mt-1 gap-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                    onClick={() => {
                      const f = fournisseurs.find(x => x.code === form.fournisseur_code);
                      if (f) navigate(`/fournisseurs?id=${f.id}`);
                      else toast.info('Créez d\'abord ce fournisseur dans le module Fournisseurs');
                    }}
                    data-testid="btn-voir-fiche-fournisseur"
                  >
                    <User className="h-3.5 w-3.5" /> Voir fiche fournisseur
                  </Button>
                )}
              </div>
            </StepCard>

            {/* 2. Bon de commande */}
            <StepCard n={2} title="Bon de commande" icon={FileText} active={currentStep === 2} tint="bg-indigo-50">
              <div className="space-y-2">
                <Field label="N° Bon de commande">
                  <Input value={form.numero || '(auto)'} disabled />
                </Field>
                <Field label="Date de commande *">
                  <Input type="date" value={form.date_commande} onChange={e => set('date_commande', e.target.value)} />
                </Field>
                <Field label="Devise *">
                  <Select value={form.devise} onValueChange={v => set('devise', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="XOF - Franc CFA">XOF - Franc CFA</SelectItem>
                      <SelectItem value="EUR - Euro">EUR - Euro</SelectItem>
                      <SelectItem value="USD - Dollar">USD - Dollar</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Conditions de paiement">
                  <Select value={form.conditions_paiement} onValueChange={v => set('conditions_paiement', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Paiement comptant">Paiement comptant</SelectItem>
                      <SelectItem value="30 jours fin de mois">30 jours fin de mois</SelectItem>
                      <SelectItem value="45 jours">45 jours</SelectItem>
                      <SelectItem value="60 jours">60 jours</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Date d'échéance">
                  <Input type="date" value={form.date_echeance} onChange={e => set('date_echeance', e.target.value)} />
                </Field>
                <Field label="Référence fournisseur">
                  <Input value={form.reference_fournisseur} onChange={e => set('reference_fournisseur', e.target.value)} placeholder="Réf. optionnelle" />
                </Field>
              </div>
            </StepCard>

            {/* 3. Articles */}
            <StepCard n={3} title="Articles" icon={Package} active={currentStep === 3} tint="bg-emerald-50" wide>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ minWidth: '460px' }}>
                  <thead>
                    <tr className="text-left text-gray-600 bg-slate-50">
                      <th className="px-2 py-1.5 w-8">#</th>
                      <th className="px-2 py-1.5">Article</th>
                      <th className="px-2 py-1.5 text-center w-16">Qté</th>
                      <th className="px-2 py-1.5 text-right w-28">PU (FCFA)</th>
                      <th className="px-2 py-1.5 text-right w-24">Total</th>
                      <th className="px-2 py-1.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.lignes.map((l, idx) => {
                      const ht = Number(l.prix_unitaire || 0) * Number(l.quantite || 0) * (1 - Number(l.remise_pct || 0) / 100);
                      return (
                        <tr key={idx} className="border-t align-top">
                          <td className="px-2 py-2 text-slate-500 text-xs">{idx + 1}</td>
                          <td className="px-2 py-2">
                            <Input className="mb-1 h-9 text-sm" value={l.reference} onChange={e => setLigne(idx, 'reference', e.target.value)} placeholder="Code / référence" data-testid={`ligne-ref-${idx}`} />
                            <Input className="h-9 text-sm" value={l.designation} onChange={e => setLigne(idx, 'designation', e.target.value)} placeholder="Désignation article" data-testid={`ligne-designation-${idx}`} />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <Input className="h-9 text-sm text-center" type="number" min="0" value={l.quantite} onChange={e => setLigne(idx, 'quantite', e.target.value)} data-testid={`ligne-qte-${idx}`} />
                          </td>
                          <td className="px-2 py-2 text-right">
                            <Input className="h-9 text-sm text-right" type="number" min="0" value={l.prix_unitaire} onChange={e => setLigne(idx, 'prix_unitaire', e.target.value)} data-testid={`ligne-pu-${idx}`} />
                          </td>
                          <td className="px-2 py-2 text-right font-bold text-[#0B1E42] text-sm">{fmtXAF(ht)}</td>
                          <td className="px-2 py-2 text-right">
                            <button onClick={() => removeLigne(idx)} className="text-red-500 hover:text-red-700" data-testid={`ligne-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Button size="sm" variant="outline" className="mt-2 w-full gap-1 border-dashed" onClick={addLigne} data-testid="btn-add-ligne">
                <Plus className="h-3.5 w-3.5" /> Ajouter un article
              </Button>
              <div className="mt-3 space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-gray-500">Sous-total</span><b>{fmtXAF(totals.sous_total)}</b></div>
                <div className="flex justify-between"><span className="text-gray-500">TVA (18%)</span><b>{fmtXAF(totals.tva)}</b></div>
                <div className="flex justify-between text-sm mt-1 pt-1 border-t"><span className="font-semibold text-blue-600">Total TTC</span><b className="text-blue-600">{fmtXAF(totals.total_ttc)}</b></div>
              </div>
            </StepCard>

            {/* 4. Validation */}
            <StepCard n={4} title="Validation" icon={Shield} active={currentStep === 4} tint="bg-amber-50">
              <div className="space-y-2">
                <Field label="Statut">
                  <div>
                    <Badge className={`${form.validation_statut === 'valide' ? 'bg-emerald-500' : form.validation_statut === 'rejete' ? 'bg-red-500' : 'bg-amber-400'} text-white`}>
                      {form.validation_statut === 'valide' ? 'Validé' : form.validation_statut === 'rejete' ? 'Rejeté' : 'En attente de validation'}
                    </Badge>
                  </div>
                </Field>
                <Field label="Validé par">
                  <Input value={form.validation_par} onChange={e => set('validation_par', e.target.value)} placeholder="—" />
                </Field>
                <Field label="Date de validation">
                  <Input type="date" value={form.validation_date} onChange={e => set('validation_date', e.target.value)} />
                </Field>
                <Field label="Commentaires">
                  <Textarea rows={4} value={form.validation_commentaires} onChange={e => set('validation_commentaires', e.target.value)} placeholder="Commentaires de validation" />
                </Field>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => { set('validation_statut', 'valide'); set('validation_date', new Date().toISOString().slice(0, 10)); }}>Valider</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => set('validation_statut', 'rejete')}>Rejeter</Button>
                </div>
              </div>
            </StepCard>
          </div>

          {/* Row 2 : 5 Paiement + 6 Expédition + 7 Livraison */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {/* 5. Paiement */}
            <StepCard n={5} title="Paiement" icon={DollarSign} active={currentStep === 5} tint="bg-emerald-50">
              <div className="space-y-2">
                <Field label="Statut paiement">
                  <Badge className={`${form.paiement_statut === 'paye' ? 'bg-emerald-500' : form.paiement_statut === 'partiel' ? 'bg-amber-400' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'}`}>
                    {form.paiement_statut === 'paye' ? 'Payé' : form.paiement_statut === 'partiel' ? 'Partiel' : 'Non payé'}
                  </Badge>
                </Field>
                <Field label="Mode de paiement">
                  <Select value={form.paiement_mode} onValueChange={v => set('paiement_mode', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Virement bancaire">Virement bancaire</SelectItem>
                      <SelectItem value="Chèque">Chèque</SelectItem>
                      <SelectItem value="Espèces">Espèces</SelectItem>
                      <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Acompte (FCFA)">
                  <Input type="number" value={form.paiement_acompte} onChange={e => set('paiement_acompte', e.target.value)} />
                </Field>
                <div className="flex justify-between text-sm mt-2 pt-2 border-t">
                  <span className="text-gray-500">Reste à payer</span>
                  <b className="text-emerald-700">{fmtXAF(totals.reste)} FCFA</b>
                </div>
                <Button size="sm" className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => set('paiement_statut', 'paye')}>
                  Enregistrer le paiement
                </Button>
              </div>
            </StepCard>

            {/* 6. Expédition */}
            <StepCard n={6} title="Expédition" icon={Send} active={currentStep === 6} tint="bg-violet-50">
              <div className="space-y-2">
                <Field label="Statut expédition">
                  <Badge className={`${form.expedition_statut === 'expedee' ? 'bg-violet-500 text-white' : 'bg-violet-100 text-violet-700 hover:bg-violet-100'}`}>
                    {form.expedition_statut === 'expedee' ? 'Expédiée' : form.expedition_statut === 'preparee' ? 'Préparée' : 'En attente'}
                  </Badge>
                </Field>
                <Field label="Date d'expédition prévue">
                  <Input type="date" value={form.date_expedition_prevue} onChange={e => set('date_expedition_prevue', e.target.value)} />
                </Field>
                <Field label="Transporteur">
                  <Input value={form.transporteur} onChange={e => set('transporteur', e.target.value)} placeholder="DHL Sénégal" />
                </Field>
                <Field label="N° de suivi / Tracking">
                  <Input value={form.numero_suivi} onChange={e => set('numero_suivi', e.target.value)} placeholder="DHLSN123456789" />
                </Field>
                <Button size="sm" className="mt-2 w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={() => set('expedition_statut', 'expedee')}>
                  Marquer comme expédiée
                </Button>
              </div>
            </StepCard>

            {/* 7. Livraison */}
            <StepCard n={7} title="Livraison" icon={Truck} active={currentStep === 7} tint="bg-cyan-50">
              <div className="space-y-2">
                <Field label="Statut livraison">
                  <Badge className={`${form.livraison_statut === 'livree' ? 'bg-cyan-600 text-white' : 'bg-cyan-100 text-cyan-700 hover:bg-cyan-100'}`}>
                    {form.livraison_statut === 'livree' ? 'Livrée' : form.livraison_statut === 'partielle' ? 'Partielle' : 'Non livrée'}
                  </Badge>
                </Field>
                <Field label="Date de livraison prévue">
                  <Input type="date" value={form.date_livraison_prevue} onChange={e => set('date_livraison_prevue', e.target.value)} />
                </Field>
                <Field label="Adresse de livraison">
                  <Textarea rows={2} value={form.lieu_livraison} onChange={e => set('lieu_livraison', e.target.value)} placeholder="Magasin Central SESAM…" />
                </Field>
                <Field label="Date de livraison réelle">
                  <Input type="date" value={form.date_livraison_reelle} onChange={e => set('date_livraison_reelle', e.target.value)} />
                </Field>
                <Field label="Livré par">
                  <Input value={form.livre_par} onChange={e => set('livre_par', e.target.value)} placeholder="—" />
                </Field>
                <Field label="Réceptionné par">
                  <Input value={form.receptionne_par} onChange={e => set('receptionne_par', e.target.value)} placeholder="—" />
                </Field>
                <Button size="sm" className="mt-2 w-full bg-cyan-600 hover:bg-cyan-700 text-white" onClick={() => { set('livraison_statut', 'livree'); set('statut', 'livree'); set('date_livraison_reelle', new Date().toISOString().slice(0, 10)); }}>
                  Confirmer la livraison
                </Button>
              </div>
            </StepCard>
          </div>

          {/* Observations */}
          <Card>
            <CardContent className="p-4">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Observations</Label>
              <Textarea rows={2} value={form.observations} onChange={e => set('observations', e.target.value)} placeholder="Notes qui apparaîtront sur le bon de commande PDF" className="mt-1" />
            </CardContent>
          </Card>

          {/* Info banner bas de page */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 flex items-center gap-3">
              <Shield className="h-4 w-4 text-blue-600 flex-shrink-0" />
              <p className="text-xs text-blue-900">Veuillez compléter toutes les étapes de la saisie de commande jusqu&apos;à la livraison pour finaliser le processus.</p>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar : Résumé */}
        <div className="lg:col-span-3">
          <Card className="sticky top-4 border-blue-200 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-bold text-[#0B1E42]">Résumé</div>
                  <div className="text-xs text-gray-500">de la commande</div>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <SummaryRow label="Fournisseur" value={form.fournisseur_nom || '—'} />
                <SummaryRow label="N° Commande" value={form.numero || '(à créer)'} />
                <SummaryRow label="Date commande" value={form.date_commande || '—'} />
                <div className="border-t pt-3 space-y-2">
                  <SummaryRow label="Total HT" value={`${fmtXAF(totals.sous_total)} FCFA`} />
                  <SummaryRow label="TVA (18%)" value={`${fmtXAF(totals.tva)} FCFA`} />
                  <div className="border-t pt-2">
                    <div className="text-xs text-gray-500 mb-0.5">Total TTC</div>
                    <div className="text-2xl font-black text-blue-600" data-testid="summary-total-ttc">{fmtXAF(totals.total_ttc)} FCFA</div>
                  </div>
                </div>
                <div className="border-t pt-3">
                  <div className="text-xs text-gray-500 mb-1">Statut global</div>
                  <Badge className={`${STATUS_LABELS[form.statut]?.color || 'bg-slate-500'} text-white`}>
                    {STATUS_LABELS[form.statut]?.label || 'En cours'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Composants utilitaires
// ─────────────────────────────────────────────────────────────────────────────
function StepCard({ n, title, icon: Icon, active, tint, wide, children }) {
  return (
    <Card className={`transition-all ${active ? 'ring-2 ring-blue-500 shadow-lg' : 'shadow-sm'} ${wide ? 'md:col-span-2 xl:col-span-2' : ''}`}>
      <div className={`${tint} px-3 py-2 border-b flex items-center gap-2 rounded-t-lg`}>
        <div className={`w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold ${active ? 'bg-blue-600' : 'bg-[#0B1E42]'}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="text-sm font-bold text-[#0B1E42]">
          <span className="text-blue-600 mr-1">{n}.</span>{title}
        </div>
      </div>
      <CardContent className="p-3">{children}</CardContent>
    </Card>
  );
}

function Field({ label, children, muted }) {
  if (muted) return <Label className="text-xs font-semibold text-gray-500">{label}</Label>;
  return (
    <div>
      <Label className="text-xs text-gray-600 mb-0.5 block">{label}</Label>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="font-semibold text-[#0B1E42] text-sm truncate">{value}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page principale
// ─────────────────────────────────────────────────────────────────────────────
export default function SaisieCommande({ user, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // 'list' | 'edit'
  const [editingId, setEditingId] = useState(null);
  const [editingInitial, setEditingInitial] = useState(null);
  const [searchParams] = useSearchParams();
  const [shareBc, setShareBc] = useState(null);
  const [fournisseurs, setFournisseurs] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/commandes`);
      setItems(data || []);
    } catch (e) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    axios.get(`${API}/api/fournisseurs`).then((r) => setFournisseurs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const phoneOf = (bc) => {
    const f = fournisseurs.find((x) => (bc.fournisseur_id && x.id === bc.fournisseur_id) || (bc.fournisseur_code && x.code === bc.fournisseur_code) || x.nom === bc.fournisseur_nom);
    return f?.telephone || '';
  };

  useEffect(() => {
    if (searchParams.get('new') === '1') {
      setMode('edit');
      setEditingId(null);
      setEditingInitial(null);
    }
  }, [searchParams]);

  const onNew = () => { setEditingId(null); setEditingInitial(null); setMode('edit'); };
  const onOpen = (bc) => { setEditingId(bc.id); setEditingInitial(bc); setMode('edit'); };
  const onCancel = () => { setMode('list'); load(); };
  const onSaved = () => { setMode('list'); load(); };

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer ce bon de commande ?')) return;
    try {
      await axios.delete(`${API}/api/commandes/${id}`);
      toast.success('Bon de commande supprimé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const onPdf = async (bc) => {
    try {
      const res = await axios.get(`${API}/api/commandes/${bc.id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bon-commande-${bc.numero}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) { toast.error('Erreur lors du téléchargement du PDF'); }
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="saisie-commande">
      <div data-testid="saisie-commande-page">
        {mode === 'list' ? (
          loading ? (
            <Card><CardContent className="p-12 text-center text-gray-500">Chargement…</CardContent></Card>
          ) : (
            <ListView items={items} onNew={onNew} onOpen={onOpen} onDelete={onDelete} onPdf={onPdf} onSend={setShareBc} />
          )
        ) : (
          <EditorView initial={editingInitial} editingId={editingId} onCancel={onCancel} onSaved={onSaved} />
        )}
      </div>
      <ShareDocDialog
        open={!!shareBc}
        onOpenChange={(v) => { if (!v) setShareBc(null); }}
        kind="bon_commande"
        docId={shareBc?.id}
        docLabel={`le bon de commande ${shareBc?.numero || shareBc?.objet || shareBc?.fournisseur_nom || ''}`}
        defaultPhone={shareBc ? phoneOf(shareBc) : ''}
      />
    </Layout>
  );
}
